import { Button } from '@shared/components/shadcn';
import { Error } from '@shared/icons/system';
import { useTranslations } from 'next-intl';
import { usePaymentLimitImpressionTracking } from '../../../hooks/usePaymentLimitImpressionTracking';
import type { ErrorStepProps } from '../../types';
import { buildErrorStepWrapperProps } from '../../utils/buildErrorStepWrapperProps';

function ErrorStep({
  errorMessage,
  titleKey,
  messageKey,
  errorKey,
  onRetry,
  showRetry = true,
  secondaryAction,
  dialogTracking,
  paymentFailureTracking,
  errorTrackContainer,
}: ErrorStepProps) {
  const t = useTranslations('global_sheets.checkout.error_step');
  const impressionName = usePaymentLimitImpressionTracking(errorKey);
  const message = errorMessage ?? (messageKey ? t(messageKey) : t('description'));
  const title = titleKey ? t(titleKey) : t('title');

  const content = (
    <>
      <div
        className="flex flex-col gap-5"
        {...(dialogTracking
          ? {
              'data-track-container': 'Dialog',
              'data-track-type': dialogTracking.dataTrackType,
              'data-track-message': dialogTracking.dataTrackMessage,
            }
          : {})}
      >
        <Error size="extra-extra-large" />
        <div className="flex flex-col gap-2">
          <h4>{title}</h4>
          <p className="text-foreground-subtle" data-track-name="Reason">
            {message}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-4 mt-auto" data-track-container="Actions">
        {showRetry && onRetry ? (
          <Button type="button" variant="secondary" onClick={onRetry} className="w-full h-10" data-testid="checkout-retry-button" data-track-name="Retry">
            {t('retry_button')}
          </Button>
        ) : null}

        {secondaryAction ? (
          <Button
            type="button"
            variant="ghost"
            onClick={secondaryAction.onClick}
            className="w-full h-10"
            data-testid="checkout-error-secondary-action"
            data-track-name="SecondaryAction"
          >
            {t(secondaryAction.labelKey)}
          </Button>
        ) : null}
      </div>
    </>
  );

  return (
    <div {...buildErrorStepWrapperProps({ paymentFailureTracking, errorTrackContainer, dialogTracking })} {...(impressionName ? { 'data-impression': impressionName } : {})}>
      {content}
    </div>
  );
}

export { ErrorStep };
